import type { MouseEvent, ReactElement } from 'react'
import { useEffect, useState } from 'react'
import { chartCss } from '~/charts/theme'
import { AcceptInvitation } from '~/components/AcceptInvitation'
import { ProvenanceBanner } from '~/components/ProvenanceBanner'
import { AppBar } from '~/components/AppBar'
import { Button, Menu, Tabs } from '~/components/ui/Controls'
import { Avatar } from '~/components/ui/Status'
import {
  ACCOUNT_PATH,
  ASIDES,
  DESTINATIONS,
  LOGIN_PATH,
  SETTINGS_PATH,
  isAppPath,
  landingFor,
  movedTo,
  useCapabilities,
  visibleTabs,
} from '~/navigation'
import { Login } from '~/pages/Login'
import { AddHoldingDialog } from '~/pages/Manual'
import type { Session } from '~/state/auth'
import { invitationFromLocation, useSession, useSignOut, useSwitchTenant } from '~/state/auth'

export function App(): ReactElement {
  const [path, setPath] = useState<string>(() => window.location.pathname)
  const [intended, setIntended] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const { status, session, error } = useSession()
  // Asked here rather than where the tabs are drawn, because a hook cannot
  // wait for the gate below to decide whether there is a household to ask
  // about — `enabled` does that waiting instead, so nothing is requested
  // before the session is.
  const capabilities = useCapabilities(
    status === 'authenticated' && session !== null && session.tenants.length > 0,
  )

  /**
   * Read once, at the address this tab opened on — before the redirect to
   * `LOGIN_PATH` below rewrites the path, and before the resume effect further
   * down drops the query entirely. Registering redeems the token itself and
   * clears this the moment it does; anyone who arrives signed in with it still
   * set, however they got there, has not.
   */
  const [pendingInvitation, setPendingInvitation] = useState<string | undefined>(
    invitationFromLocation,
  )

  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = (to: string, replace = false): void => {
    if (replace) window.history.replaceState(null, '', to)
    else window.history.pushState(null, '', to)
    setPath(to)
  }

  /**
   * An address that has moved is corrected in place rather than pushed, so the
   * back button leaves the app instead of bouncing off the redirect it just
   * followed.
   */
  useEffect(() => {
    const to = movedTo(path)
    if (to !== undefined) {
      window.history.replaceState(null, '', to)
      setPath(to)
    }
  }, [path])

  /**
   * Links written inside a page point at the app's own addresses too. Letting
   * the browser follow one would drop the session, the query cache and every
   * figure already fetched, to arrive at the same React tree.
   */
  useEffect(() => {
    const onClick = (event: globalThis.MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = event.target instanceof Element ? event.target.closest('a') : null
      if (anchor === null || anchor.target !== '') return
      if (anchor.origin !== window.location.origin || !isAppPath(anchor.pathname)) return

      event.preventDefault()
      go(anchor.pathname)
    }

    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  /**
   * The gate. A page that renders while the session is still a question would
   * fire its own queries first, and the answer to those is somebody's money.
   * Where the visitor was headed is kept so that signing in resumes the link
   * they followed rather than dropping them on the front page.
   */
  useEffect(() => {
    if (status === 'loading') return

    if (status !== 'authenticated') {
      if (path !== LOGIN_PATH) {
        setIntended(path)
        // The query comes with it. An invitation is handed out as a link to the
        // front page carrying its token, and a redirect that kept only the path
        // took the token off the address before the page that reads it opened.
        window.history.replaceState(null, '', `${LOGIN_PATH}${window.location.search}`)
        setPath(LOGIN_PATH)
      }
      return
    }

    if (path === LOGIN_PATH) {
      const resume = intended ?? '/'
      setIntended(null)
      window.history.replaceState(null, '', resume)
      setPath(resume)
    }
  }, [status, path, intended])

  const navigate = (event: MouseEvent<HTMLAnchorElement>, to: string): void => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    go(to)
  }

  if (status === 'loading') {
    return (
      <main className="nw">
        <style>{chartCss}</style>
        {error ? (
          // A session request that failed is not a session that said no. Without
          // this the page sits on "Checking…" for ever, which reads as slow
          // rather than as a server that is not answering.
          <p className="nw-flag nw-flag--missing">
            <span className="nw-flag-icon" aria-hidden="true">
              ✕
            </span>
            <span>
              <b>Could not reach the dashboard</b> — asking who you are failed with:{' '}
              {error.message}. Nothing is shown rather than a page that might be anyone's.
            </span>
          </p>
        ) : (
          <p className="nw-empty">Checking who you are…</p>
        )}
      </main>
    )
  }

  if (status !== 'authenticated' || session === null) {
    return (
      <>
        <style>{chartCss}</style>
        <Login onInvitationRedeemed={() => setPendingInvitation(undefined)} />
      </>
    )
  }

  // Resolved for the render as well as for the address bar, so an old link
  // paints its new home rather than flashing the Overview on the way there.
  const here = movedTo(path) ?? path
  const aside = ASIDES[here]
  const { destination, tab } = landingFor(here)
  const tabs = visibleTabs(destination, capabilities, tab.id)
  const household = session.tenants.length > 0

  return (
    <>
      <style>{chartCss}</style>
      <ProvenanceBanner />
      <AppBar
        destinations={DESTINATIONS}
        current={aside === undefined ? destination.path : ''}
        onNavigate={navigate}
        action={
          household ? (
            <Button kind="primary" aria-label="Add holding" onClick={() => setAdding(true)}>
              {/*
                Four destinations and a word-for-word button do not both fit in
                the bar a phone gets: at 390 the last one is cut in half. The
                label is the mark instead, and the name stays on the element.
              */}
              <span className="nw-add-mark" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="nw-add-word">Add holding</span>
            </Button>
          ) : undefined
        }
        who={<Who session={session} path={path} onNavigate={navigate} />}
      />
      {aside === undefined && tabs.length > 1 && (
        <div className="nw-subnav">
          <div className="nw-subnav-inner">
            <Tabs
              label={destination.label}
              tabs={tabs}
              value={tab.id}
              onChange={(id) => {
                const chosen = tabs.find((candidate) => candidate.id === id)
                if (chosen !== undefined) go(chosen.path)
              }}
            />
          </div>
        </div>
      )}
      {aside !== undefined ? (
        aside()
      ) : !household ? (
        <NoHousehold />
      ) : (
        <div role="tabpanel" id={`nw-panel-${tab.id}`} aria-labelledby={`nw-tab-${tab.id}`}>
          {tab.render()}
        </div>
      )}
      {adding && <AddHoldingDialog onClose={() => setAdding(false)} />}
      {pendingInvitation !== undefined && (
        <AcceptInvitation
          token={pendingInvitation}
          email={session.user.email}
          onDone={() => setPendingInvitation(undefined)}
        />
      )}
    </>
  )
}

/**
 * A sign-in with no membership is not an error, and it is not a dashboard
 * either: every figure route answers it 403, so saying so once beats ten
 * pages each failing in their own words.
 *
 * It used to name an invitation as the only way out, which left the reader
 * waiting on somebody else in front of a door they can open themselves —
 * Account starts a household, gates on nothing, and opens it immediately.
 */
function NoHousehold(): ReactElement {
  return (
    <main className="nw">
      <p className="nw-empty">
        You are signed in, but you do not belong to a household yet. Start one of your own from{' '}
        <a href={ACCOUNT_PATH}>Account</a>, or wait for an owner to invite you into theirs.
      </p>
    </main>
  )
}

/** The first two letters of the address, which is all this product knows of a name. */
function initialsOf(email: string): string {
  const local = email.split('@')[0] ?? email
  const letters = local.replace(/[^a-zA-Z]/g, '')
  return (letters.slice(0, 2) || local.slice(0, 2)).toUpperCase()
}

const ASIDE_ROUTES: readonly { path: string; label: string }[] = [
  { path: ACCOUNT_PATH, label: 'Account' },
  { path: SETTINGS_PATH, label: 'Settings' },
]

/**
 * Everything a person does to their own account, in one place behind the
 * avatar. None of it is a figure, and while each had a pill of its own the
 * navigation was answering two questions at once: what am I looking at, and
 * who am I looking as.
 */
function Who({
  session,
  path,
  onNavigate,
}: {
  session: Session
  path: string
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, to: string) => void
}): ReactElement {
  const signOut = useSignOut()

  return (
    <Menu label="Your account" trigger={<Avatar label={session.user.email} initials={initialsOf(session.user.email)} />}>
      {(close) => (
        <>
          <p className="nw-menu-who">{session.user.email}</p>
          <Households session={session} onChosen={close} />
          <hr className="nw-menu-rule" />
          {ASIDE_ROUTES.map((route) => (
            <a
              key={route.path}
              role="menuitem"
              tabIndex={-1}
              href={route.path}
              aria-current={path === route.path ? 'page' : undefined}
              onClick={(event) => {
                onNavigate(event, route.path)
                close()
              }}
            >
              {route.label}
            </a>
          ))}
          <hr className="nw-menu-rule" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            Sign out
          </button>
        </>
      )}
    </Menu>
  )
}

/**
 * A person can belong to several households, and the figures of two of them are
 * never shown together — choosing one is choosing what the whole dashboard is
 * about. A single membership is stated rather than offered, because a choice
 * with one entry reads as a choice that exists.
 */
function Households({
  session,
  onChosen,
}: {
  session: Session
  onChosen: () => void
}): ReactElement | null {
  const switchTenant = useSwitchTenant()
  const [only] = session.tenants

  if (session.tenants.length === 0) return null

  // Labelled even when there is only one, because a household created from an
  // address is named after it: without the label the panel says the same
  // address twice and reads as a mistake rather than as two facts.
  if (session.tenants.length === 1 && only !== undefined) {
    return (
      <>
        <p className="nw-menu-label">Household</p>
        <p className="nw-menu-who">{only.name}</p>
      </>
    )
  }

  return (
    <>
      <p className="nw-menu-label">Household</p>
      {session.tenants.map((membership) => (
        <button
          key={membership.tenantId}
          type="button"
          role="menuitemradio"
          tabIndex={-1}
          aria-checked={membership.tenantId === session.tenantId}
          disabled={switchTenant.isPending}
          onClick={() => {
            switchTenant.mutate(membership.tenantId)
            onChosen()
          }}
        >
          {membership.name}
        </button>
      ))}
    </>
  )
}
